/**
 * AuthResetPage.tsx — Supabase password-reset completion handler.
 *
 * Supabase redirects here after a user clicks the reset link in their email.
 * The URL contains a recovery token that supabase-js exchanges for a session
 * automatically (detectSessionInUrl=true in the client config), firing the
 * PASSWORD_RECOVERY auth event.
 *
 * This page:
 *  1. Detects the PASSWORD_RECOVERY event (or checks the existing session
 *     if the page loads after the event has already fired).
 *  2. Shows a "Set new password" form.
 *  3. Calls supabase.auth.updateUser({ password }) on submit.
 *  4. Redirects to /login with a success message.
 *
 * Route: /auth/reset  (registered in App.tsx)
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

type Phase = 'checking' | 'ready' | 'success' | 'error';

const inputStyle = (disabled: boolean): React.CSSProperties => ({
  width: '100%', background: disabled ? 'rgba(6,15,31,0.6)' : '#060f1f',
  border: '1.5px solid rgba(59,130,246,0.22)', borderRadius: 8,
  color: disabled ? '#4b6fa8' : '#e2e8f0', fontSize: 14,
  padding: '13px 44px 13px 44px', outline: 'none',
  boxSizing: 'border-box' as const, fontFamily: 'inherit',
  cursor: disabled ? 'not-allowed' : 'text',
});

export default function AuthResetPage() {
  const [phase,      setPhase]      = useState<Phase>('checking');
  const [password,   setPassword]   = useState('');
  const [confirm,    setConfirm]    = useState('');
  const [showPass,   setShowPass]   = useState(false);
  const [showConf,   setShowConf]   = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [formError,  setFormError]  = useState<string | null>(null);
  const [, setLocation] = useLocation();

  useEffect(() => {
    // Listen for PASSWORD_RECOVERY event — supabase-js fires this after it
    // exchanges the recovery token from the URL for a short-lived session.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        if (event === 'PASSWORD_RECOVERY') {
          setPhase('ready');
        }
      },
    );

    // Fallback: if the page loads after the event already fired (fast navigation),
    // check whether a session is present — recovery tokens create an active session.
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setPhase('ready');
      } else {
        // Wait up to 6 s for the PASSWORD_RECOVERY event; then show an error.
        fallbackTimer = setTimeout(() => {
          setPhase('error');
        }, 6000);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setFormError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setFormError(error.message || 'Failed to update password. Please try again.');
        return;
      }
      setPhase('success');
      // Sign out so the user logs in fresh with the new password.
      await supabase.auth.signOut();
      setTimeout(() => setLocation('/login'), 2000);
    } catch {
      setFormError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse 80% 70% at 50% 35%, #0b2060 0%, #04112b 55%, #010a1a 100%)',
      padding: '20px 16px', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{
        width: '100%', maxWidth: 430, boxSizing: 'border-box' as const,
        padding: 'clamp(28px,6vw,44px) clamp(20px,5.5vw,36px)',
        background: 'linear-gradient(175deg, #0d2048 0%, #071428 60%, #040d1e 100%)',
        borderRadius: 20, border: '1.5px solid rgba(59,130,246,0.45)',
        boxShadow: '0 0 40px rgba(37,99,235,0.30), inset 0 1px 0 rgba(255,255,255,0.04)',
        display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
      }}>
        <img
          src="/workroamer-brand.png"
          alt="WorkRoamer"
          style={{ height: 40, width: 'auto', objectFit: 'contain', marginBottom: 24 }}
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />

        {/* ── Checking / loading phase ───────────────────────────────────────── */}
        {phase === 'checking' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Loader2 size={36} color="#3b82f6" style={{ animation: 'spin 1s linear infinite', marginBottom: 16 }} />
            <p style={{ color: '#8fa8c8', fontSize: 14, margin: 0 }}>Verifying your reset link…</p>
          </div>
        )}

        {/* ── Error / expired token ─────────────────────────────────────────── */}
        {phase === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#f87171', fontSize: 15, fontWeight: 700, margin: '0 0 8px' }}>
              Link expired or invalid
            </p>
            <p style={{ color: '#8fa8c8', fontSize: 13, margin: '0 0 20px', lineHeight: 1.5 }}>
              This password reset link has expired. Please request a new one.
            </p>
            <a
              href="/forgot-password"
              style={{
                display: 'inline-block', padding: '11px 24px',
                background: 'linear-gradient(90deg, #1a3fcc 0%, #2563eb 100%)',
                color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700,
                textDecoration: 'none', letterSpacing: '0.05em',
              }}
            >
              Request New Reset Link
            </a>
          </div>
        )}

        {/* ── Success ───────────────────────────────────────────────────────── */}
        {phase === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle2 size={52} color="#22c55e" strokeWidth={1.5}
              style={{ filter: 'drop-shadow(0 0 20px rgba(34,197,94,0.5))', marginBottom: 16 }} />
            <p style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
              Password updated!
            </p>
            <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
              Redirecting you to sign in…
            </p>
          </div>
        )}

        {/* ── Form ─────────────────────────────────────────────────────────── */}
        {phase === 'ready' && (
          <>
            <p style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 700, margin: '0 0 6px', textAlign: 'center' }}>
              Set new password
            </p>
            <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 24px', textAlign: 'center' }}>
              Choose a new password for your WorkRoamer account.
            </p>

            <form onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* New password */}
              <div>
                <label style={{ display: 'block', color: '#3b82f6', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' as const, marginBottom: 8 }}>
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#4b6fa8', pointerEvents: 'none' }} />
                  <input
                    type={showPass ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    disabled={loading}
                    style={inputStyle(loading)}
                    onFocus={e => { if (!loading) e.currentTarget.style.borderColor = 'rgba(59,130,246,0.65)'; }}
                    onBlur={e  => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.22)')}
                  />
                  <button type="button" onClick={() => setShowPass(v => !v)} tabIndex={-1}
                    style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#4b6fa8', cursor: 'pointer', padding: 0, display: 'flex' }}>
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div>
                <label style={{ display: 'block', color: '#3b82f6', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' as const, marginBottom: 8 }}>
                  Confirm Password
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#4b6fa8', pointerEvents: 'none' }} />
                  <input
                    type={showConf ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Repeat your new password"
                    disabled={loading}
                    style={inputStyle(loading)}
                    onFocus={e => { if (!loading) e.currentTarget.style.borderColor = 'rgba(59,130,246,0.65)'; }}
                    onBlur={e  => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.22)')}
                  />
                  <button type="button" onClick={() => setShowConf(v => !v)} tabIndex={-1}
                    style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#4b6fa8', cursor: 'pointer', padding: 0, display: 'flex' }}>
                    {showConf ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {formError && (
                <p style={{ color: '#f87171', fontSize: 12, textAlign: 'center', margin: 0 }}>{formError}</p>
              )}

              <button type="submit" disabled={loading} style={{
                width: '100%',
                background: loading ? 'rgba(37,99,235,0.45)' : 'linear-gradient(90deg, #1a3fcc 0%, #2563eb 45%, #3b82f6 100%)',
                border: 'none', borderRadius: 8, color: '#fff',
                fontSize: 13, fontWeight: 800, letterSpacing: '0.16em',
                textTransform: 'uppercase' as const, padding: '16px 24px',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: loading ? 'none' : '0 4px 18px rgba(37,99,235,0.45)',
                fontFamily: 'inherit', opacity: loading ? 0.65 : 1,
              }}>
                {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                {loading ? 'Updating…' : 'Set New Password'}
              </button>
            </form>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
